import { lazy, Suspense, useEffect, useMemo, type ReactNode } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { BrandLoader } from '@/components/lumen/BrandLoader';
import { useLoaderPhase } from '@/lib/use-loader-phase';
import { Icon } from '@/components/lumen/Icon';
import { ThemeMenu } from '@/components/lumen/ThemeMenu';
import { cn } from '@/lib/utils';
import { NAV_GROUPS } from './nav';
import { useNav } from './theme';
import { BrandWordmark } from '@/components/lumen/BrandWordmark';
import { Breadcrumb } from '@/components/lumen/Breadcrumb';
import { notShipped } from '@/product/shipped';
/* Type-only, and erased: the two chrome pieces' shapes without putting them in the entry graph. */
import type * as QuickLinksModule from '@/components/lumen/QuickLinks';
import type * as AiDockModule from '@/components/lumen/AiDock';
import type * as ThemeRuntimeModule from '@/modules/appearance/theme-runtime';
import { PRODUCT } from '@/product/profile';
import { useDeviceRoles } from '@/lib/api/onboarding';
import { useDocumentFullscreen } from '@/lib/use-fullscreen';

/*
 * CRATUS-ONLY CHROME IS LAZY AND BEHIND `__PRODUCT__`, so Thunder's build never emits it.
 *
 * Both used to be imported eagerly and switched off at run time by `PRODUCT.quickLinks` /
 * `PRODUCT.aiDock`. Measured 2026-10-15: that put Thunder's entry chunk at 200.2 KB gzipped,
 * over its 200 KB budget, for two components Thunder never draws. Same pattern as the lazy
 * views in `App.tsx`.
 */
const QuickLinks = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/components/lumen/QuickLinks') : notShipped<typeof QuickLinksModule>()).then((m) => ({ default: m.QuickLinks })),
);
/* The company's saved look (Lumen-BE 0109): brand tokens, typeface, background and header logo.
   Lazy and Cratus-only like the chrome above; Thunder's build never emits it. */
const OrgThemeLayer = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/appearance/theme-runtime') : notShipped<typeof ThemeRuntimeModule>()).then((m) => ({ default: m.OrgThemeLayer })),
);
const ThemedWordmark = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/appearance/theme-runtime') : notShipped<typeof ThemeRuntimeModule>()).then((m) => ({ default: m.ThemedWordmark })),
);
const AiDock = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/components/lumen/AiDock') : notShipped<typeof AiDockModule>()).then((m) => ({ default: m.AiDock })),
);

interface ShellProps {
  tab: string;
  onTab: (id: string) => void;
  /** Unresolved finding count, badged on the Findings item. */
  findingCount?: number;
  /**
   * Nav-item id -> badge count, for anything that is a queue rather than a page.
   *
   * `tone` matters: a pending device is losing data every publish cycle, so it is warn,
   * not neutral. A neutral badge reads as information; this one is a task.
   */
  badges?: Record<string, { count: number; tone: 'warn' | 'danger' } | undefined>;
  /**
   * Nav-item ids to leave OUT of the rail entirely — hidden, not disabled. Used for
   * capability-gated destinations (Remote control): a greyed entry for a feature the
   * organisation has switched off invites "how do I enable this?", and the view behind it
   * refuses politely anyway if reached by URL.
   */
  hiddenItems?: readonly string[];
  /** Rendered in the header, left of the utility buttons. The site selector lives here. */
  headerSlot?: ReactNode;
  /**
   * Beside the product's wordmark: the END CUSTOMER's brand (CRATUS 2.0 #2), when the
   * organisation in view has one. The product's mark always leads; this follows it.
   */
  brandSlot?: ReactNode;
  /**
   * Rendered LAST in the header — the rightmost control on every screen.
   *
   * A separate slot rather than the tail of `headerSlot` because the position is the point:
   * the account menu is the one control whose place an operator learns once and then reaches
   * for without looking, and it can only be learned if nothing is ever appended after it.
   * Folding it into `headerSlot` puts that guarantee in the caller's hands, where the next
   * person to add a button will quietly take it away.
   */
  accountSlot?: ReactNode;
  children: ReactNode;
}

export function Shell({
  tab,
  onTab,
  findingCount,
  badges,
  hiddenItems,
  headerSlot,
  brandSlot,
  accountSlot,
  children,
}: ShellProps): ReactNode {
  /*
   * Fetched here so the device-role catalogue is installed for EVERY screen, not only the
   * three that happen to render a role picker. `model.ts` falls back to its built-in table
   * until this lands, so nothing waits on it — but a site whose roles include a smart lock
   * should not have to visit the device page before the diagram knows what one is.
   */
  useDeviceRoles();

  const fs = useDocumentFullscreen();

  /*
   * SHIFT+F, and deliberately not plain F or F11.
   *
   * F11 belongs to the browser and fighting it would be rude and unreliable. A bare `f`
   * collides with the twin's own single-letter tool keys (R for room, O for opening, V for
   * select), and a shortcut that means one thing on eight screens and another on the ninth
   * is worse than no shortcut.
   *
   * Never while a field has focus: typing "Shift+F" into a site name must produce an F.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'F' && e.key !== 'f') return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName) || t.isContentEditable)) {
        return;
      }
      e.preventDefault();
      fs.toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fs]);
  const { collapsed, toggle: toggleNav, open, toggleGroup, seedGroups } = useNav();

  /*
   * SEED THE ACCORDION FROM WHERE THE OPERATOR ACTUALLY IS.
   *
   * On a first visit only the group containing the current page is open, so the rail shows
   * five headings and the handful of items that are relevant instead of eighteen items at
   * once. `seedGroups` is a no-op once storage holds a choice — an operator who closed
   * everything deliberately keeps it closed.
   */
  const activeGroup = useMemo(
    () => NAV_GROUPS.find((g) => g.items.some((i) => i.id === tab))?.label,
    [tab],
  );
  useEffect(() => {
    if (activeGroup !== undefined) seedGroups([activeGroup]);
  }, [activeGroup, seedGroups]);

  /*
   * THE ACTIVE GROUP IS ALWAYS OPEN, whatever storage says.
   *
   * Navigating into a page whose group the operator had collapsed would otherwise leave the
   * rail with no visible indication of where they are — every heading closed and nothing
   * highlighted. Forcing it open is not overriding their choice; it is the disclosure
   * following the navigation, which is what a drilldown is.
   */
  const isOpen = (label: string): boolean =>
    label === activeGroup || (open?.has(label) ?? false);

  return (
    /*
     * `100dvh`, not `h-screen` (which is `100vh`).
     *
     * `100vh` is the LARGEST the viewport can be — it ignores browser UI that is currently on
     * screen. On Safari, on iOS, and on any window where a toolbar is showing, the shell
     * therefore becomes TALLER than the visible area, the document itself starts scrolling, and
     * scrolling drags the header and the sidebar off the top while leaving dead space below
     * them. That is exactly the reported symptom: the nav "not sticky", with white underneath.
     *
     * `100dvh` tracks the viewport that is actually visible. The `100vh` line before it is the
     * fallback for browsers without dvh, where the old behaviour is still better than nothing.
     * `overflow: hidden` then guarantees the document never scrolls: only `main` does.
     */
    <div
      className="flex flex-col overflow-hidden"
      style={{
        /*
         * FIXED, not just tall. This is what actually stops the document scrolling.
         *
         * `height: 100dvh` + `overflow: hidden` on html/body was not enough. Highcharts'
         * accessibility module appends absolutely-positioned announcer and keyboard-proxy
         * containers, and on a 900 px viewport they landed at bottom 1007 — so the document
         * gained 107 px of scroll while every box we own measured exactly 900. Verified: a
         * wheel over the header moved `window.scrollY` to 107 and put the header at top
         * `-107 px`, sliding the header and the rail off the screen. That is the whole of
         * "extra scroll on the overview page" and "the left menu is not sticky".
         *
         * `position: fixed; inset: 0` takes the entire shell out of document flow, so no
         * descendant — ours or a library's — can extend the page. Only `main` scrolls, which
         * is the contract this layout has always claimed.
         *
         * Deliberately NOT solved by hunting Highcharts' injected nodes: those proxy
         * containers hold the focusable elements that make charts keyboard-navigable, so
         * pinning or shrinking them would trade a scrollbar for an accessibility regression.
         */
        position: 'fixed',
        inset: 0,
      }}
    >
      {/*
        * BYPASS BLOCKS (WCAG 2.4.1, Level A) — the first thing the keyboard reaches.
        *
        * Measured before this existed: fourteen Tab presses from the top of Overview and
        * focus was still outside `main`, because the header's five controls come first and
        * then every rail item in every expanded group. On a console with nine modules that
        * is roughly twenty-five stops to reach the content — on EVERY page, every time.
        *
        * Hidden until focused rather than always visible: sighted mouse users never need
        * it, and the design is deliberately dense. It has to be the first element in DOM
        * order to be the first tab stop, which is why it sits above the header rather than
        * inside it.
        */}
      {__PRODUCT__ === 'cratus' && (
        <Suspense fallback={null}>
          <OrgThemeLayer />
        </Suspense>
      )}
      <a href="#lm-main" className="lm-skip">
        Skip to content
      </a>

      {/* ------------------------------------------------------- header */}
      {/*
        * `lm-glass` — REAL `backdrop-filter`, and this is one of the four places it is
        * allowed. The header is fixed, so its backdrop never moves and the blur is
        * composited once rather than re-sampled per scroll frame. See `app.css` for why
        * `.lm-card` deliberately does not get it.
        */}
      {/* Cratus: above the quick-links rail beside the page, or the account menu and every other
          header popover open UNDER the rail (it is a later sibling with its own stacking context). */}
      <header
        className={cn('lm-glass flex shrink-0 items-center gap-[10px] border-b px-4', __PRODUCT__ === 'cratus' && 'relative z-[30]')}
        style={{ height: 'var(--header-h)' }}
      >
        {/*
          * Two-line brand, as the reference design has it. The tagline is not decoration: this
          * console sits alongside a BMS, a DCIM and an NMS on the same operator's screen, and
          * "Thunder" alone does not say which one it is. It is hidden below 640px, where the site
          * selector matters more than the product's own name.
          */}
        {/*
          * THE WORDMARK CARRIES THE NAME, so the adjacent text no longer repeats it.
          *
          * The artwork already renders "Thunder"; typesetting it again beside the mark would
          * say the name twice in 200px.
          *
          * NO TAGLINE. There was a "Power Standards Compliance" line here, and it described
          * a narrower product than this one: Thunder is a remote monitoring and control
          * platform that ALSO does standards and compliance monitoring. A subtitle that names
          * only the compliance half misrepresents it to the operator reading the header.
          *
          * ALL FOUR THEME STATES, via `BrandWordmark` rather than `<picture media>`.
          *
          * This was a `<picture>` with `media="(prefers-color-scheme: dark)"` and a single
          * `<img>`, which reads the OS preference and CANNOT see `data-theme` — the attribute
          * this console's own theme toggle writes. Measured on the real header, the two states
          * where the pair disagrees rendered the logo INVISIBLE: OS-light with the dark theme
          * picked the dark ink on --card #161618 (1.01:1), and OS-dark with the light theme
          * picked the light ink on #ffffff (1.0:1). Clicking the header's own theme button on a
          * default OS-light machine was enough to reproduce it.
          *
          * `BrandWordmark` ships both cuts and hides one in CSS, keyed on `data-ink` AND
          * `data-theme` AND `prefers-color-scheme` — so it is correct in all four combinations
          * including the system default, which sets no `data-theme` at all.
          */}
        <div className="flex min-w-0 items-center gap-[10px]">
          {__PRODUCT__ === 'cratus' ? (
            <Suspense fallback={<BrandWordmark height={22} />}>
              <ThemedWordmark height={22} />
            </Suspense>
          ) : (
            <BrandWordmark height={22} />
          )}
          {/* The hairline between the mark and the trail. It is what stops the wordmark and
              the first crumb reading as one phrase in a 60px bar. */}
          <span
            aria-hidden
            className="hidden shrink-0 sm:block"
            style={{ width: 1, height: 22, background: 'var(--border)' }}
          />
          <Breadcrumb tab={tab} homeTab={PRODUCT.homeTab} onTab={onTab} />
          {brandSlot}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {headerSlot}
          {/*
            * FULL SCREEN FOR THE WHOLE PRODUCT, and the header is the only honest place for
            * it: it is the one control that is present on every screen, and full screen is a
            * property of the window rather than of whatever view happens to be open.
            *
            * Hidden entirely when the browser does not support it — an operator pressing a
            * button that cannot work has no way to tell that from a bug. The Digital Twin
            * keeps its own stage-only control; the two are different elements, so this one
            * reports the WINDOW's state and stays correct while the stage is expanded.
            */}
          {fs.supported && (
            <button
              type="button"
              onClick={fs.toggle}
              className="lm-hov flex items-center justify-center rounded-[var(--radius-sm)]"
              style={{
                width: 'var(--header-ctl-h)',
                height: 'var(--header-ctl-h)',
                color: fs.isFullscreen ? 'var(--fg)' : 'var(--muted-fg)',
              }}
              aria-pressed={fs.isFullscreen}
              aria-label={fs.isFullscreen ? 'Leave full screen' : 'Fill the screen'}
              title={
                fs.isFullscreen
                  ? 'Leave full screen — Escape also works (Shift+F)'
                  : 'Fill the screen, hiding the browser’s own furniture (Shift+F)'
              }
            >
              <Icon name={fs.isFullscreen ? 'ic-minimize' : 'ic-maximize'} size={16} />
            </button>
          )}
          {/* Light or dark — the one appearance axis that is the PERSON's. The palette and the
              accent moved to the organisation (0058) and are stated in this menu rather than
              offered; see `ThemeMenu` for why the picker had to go rather than be disabled. */}
          {/* Cratus's light/dark and solid/glass are in its account menu, as the FESCO design has them. */}
          {__PRODUCT__ !== 'cratus' && <ThemeMenu />}

          {/*
            * THE ACCOUNT MENU IS LAST, and therefore rightmost on every screen.
            *
            * It used to sit inside `headerSlot`, ahead of full screen and the theme toggle,
            * on the reasoning that a notification badge next to "sign out" is a misclick
            * waiting to happen. The owner asked for it at the end instead, and the trade is
            * a fair one: the corner is where every other console puts an account, so it is
            * the first place an operator looks, and the two controls now between it and the
            * bell are the two least urgent things in the bar.
            *
            * The misclick worry is answered where it belongs — sign out sits at the BOTTOM
            * of the opened menu, not under the trigger.
            */}
          {accountSlot}
        </div>
      </header>

      {/*
        * A REFUSAL IS SAID OUT LOUD. `requestFullscreen` rejects when the call is not
        * user-activated, when the document is inside an iframe without
        * `allow="fullscreen"`, or under a kiosk policy — and every one of those produces a
        * button that appears broken. The operator deserves to know it was the browser's
        * decision and not ours.
        */}
      {fs.error !== null && (
        <div
          role="alert"
          className="px-[var(--page-pad-x)] py-[7px] text-[11.5px]"
          style={{
            background: 'rgb(var(--warn-rgb) / 12%)',
            borderBottom: '1px solid var(--border)',
            color: 'var(--fg)',
          }}
        >
          {fs.error}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* ----------------------------------------------------- sidebar */}
        {/* Thunder's only. Cratus navigates from Home, the breadcrumb, quick links and search
            (`PRODUCT.sidebar`); the same `hiddenItems` filters both. */}
        {PRODUCT.sidebar && (
        <aside
          className="lm-glass flex shrink-0 flex-col border-r"
          style={{
            width: collapsed
              ? 'var(--sidebar-w-collapsed)'
              : 'var(--sidebar-w)',
            transition: 'width 180ms var(--ease-press)',
            /*
             * Sticky as well as flex-constrained. The flex chain above already keeps the rail
             * in place while `main` scrolls, so this is belt and braces for the case the chain
             * is broken from outside — a routed view that sets its own `100vh` height, say,
             * which is how this class of bug gets reintroduced. If the document ever does
             * scroll, the rail stays put instead of sliding away.
             */
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100%',
          }}
        >
          <nav className="flex flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden py-2">
            {NAV_GROUPS.map((group, gi) => {
              /* Capability-hidden entries leave the rail AND the rolled-up badge sums —
                 a count for an item that cannot be opened is a task with no door. */
              const items = hiddenItems?.length
                ? group.items.filter((i) => !hiddenItems.includes(i.id))
                : group.items;
              if (items.length === 0) return null;
              /*
               * THE GROUP'S ROLLED-UP BADGE COUNT, and it is the reason this accordion is
               * safe to ship.
               *
               * A badge in this rail is not decoration — a pending device is discarding
               * measurements every publish cycle, and an unassigned feed is discarding all
               * of them. Hiding items behind a closed heading would hide those counts, so
               * an operator could sit on a page with four gateways silently dropping data
               * and see nothing. The heading therefore carries the SUM of its children's
               * badges whenever it is closed, with the worst tone winning: a danger badge
               * inside a group makes the group's badge danger, not warn.
               */
              const counts = items.map((item) => {
                const extra = badges?.[item.id];
                const n =
                  item.id === 'findings' && findingCount
                    ? findingCount
                    : (extra?.count ?? 0);
                return { n, tone: extra?.tone ?? 'danger' };
              });
              const groupTotal = counts.reduce((a, c) => a + c.n, 0);
              const groupTone = counts.some((c) => c.n > 0 && c.tone === 'danger')
                ? 'danger'
                : 'warn';
              const opened = isOpen(group.label);

              return (
              <div key={group.label} className="flex flex-col gap-px">
                {collapsed ? (
                  /*
                   * COLLAPSED RAIL HAS NO ACCORDION. At 66px there is no room for a
                   * heading, and hiding icons behind an invisible disclosure would leave
                   * the operator with a rail they cannot navigate. Icons only, divided.
                   */
                  gi > 0 && (
                    <div
                      className="mx-[14px] my-2 h-px"
                      style={{ background: 'var(--border)' }}
                    />
                  )
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                    aria-expanded={opened}
                    className="lm-hov flex w-full items-center gap-[6px] px-[15px] pb-[7px] text-left text-[10px] font-bold uppercase tracking-[0.09em]"
                    style={{
                      color: opened ? 'var(--fg)' : 'var(--muted-fg)',
                      paddingTop: gi > 0 ? 16 : 4,
                      transition: 'color var(--dur-colour)',
                    }}
                    title={
                      group.label === activeGroup
                        ? `${group.label} — contains the page you are on, so it stays open`
                        : opened
                          ? `Collapse ${group.label}`
                          : `Expand ${group.label}`
                    }
                  >
                    {/* The chevron is the affordance, so it keeps its own colour even when
                        the label is quiet. `Icon` paints `currentColor`, so the wrapper is
                        how a colour is passed to it. */}
                    <span style={{ color: 'var(--muted-fg)', display: 'flex' }}>
                      <Icon name={opened ? 'ic-chevdown' : 'ic-chevright'} size={12} />
                    </span>
                    <span className="truncate">{group.label}</span>
                    {!opened && groupTotal > 0 && (
                      <span
                        className="lm-num ml-auto rounded-[var(--radius-pill)] px-[6px] py-px text-[10px] font-bold"
                        style={{
                          background: `rgb(var(--${groupTone}-rgb) / 16%)`,
                          color: `var(--${groupTone})`,
                        }}
                        title={`${groupTotal} item(s) need attention inside ${group.label}`}
                      >
                        {groupTotal > 99 ? '99+' : groupTotal}
                      </span>
                    )}
                  </button>
                )}

                {(collapsed || opened) &&
                  items.map((item) => {
                  const active = tab === item.id;
                  const extra = badges?.[item.id];
                  const rawCount =
                    item.id === 'findings' && findingCount
                      ? findingCount
                      : (extra?.count ?? 0);
                  const badge = rawCount > 0 ? (rawCount > 99 ? '99+' : String(rawCount)) : null;
                  const badgeRgb =
                    extra?.tone === 'warn' ? 'var(--warn-rgb)' : 'var(--danger-rgb)';
                  const badgeFg = extra?.tone === 'warn' ? 'var(--warn)' : 'var(--danger)';

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onTab(item.id)}
                      title={item.label}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex h-10 w-full items-center gap-[10px] border-0 text-[13.5px]',
                        collapsed ? 'justify-center px-0' : 'justify-start px-[15px]',
                        !active && 'lm-hov',
                      )}
                      style={{
                        /*
                         * A RAIL, NOT A PILL — and now an edge-lit one.
                         *
                         * The active item carries the brand edge, a brand-tinted fill and a
                         * soft glow bleeding right off the edge. The glow is what the
                         * reference designs use to mark position, and it works here because
                         * the rail is glass over a dark ground: on an opaque panel the same
                         * gradient reads as a smudge.
                         */
                        borderLeft: `3px solid ${active ? 'var(--brand)' : 'transparent'}`,
                        background: active
                          ? 'linear-gradient(90deg, rgb(var(--brand-rgb) / 16%) 0%, rgb(var(--brand-rgb) / 3%) 62%, transparent 100%)'
                          : 'transparent',
                        color: active ? 'var(--fg)' : 'var(--muted-fg)',
                        fontWeight: active ? 600 : 500,
                        transition: 'background var(--dur-colour), color var(--dur-colour)',
                      }}
                    >
                      {/* The active item's glyph goes brand-coloured — the edge, the fill and
                          the icon all say the same thing, which is what makes position
                          readable at a glance in a 66px rail as well as a 238px one. */}
                      <span style={{ display: 'flex', color: active ? 'var(--brand)' : 'inherit' }}>
                        <Icon name={item.icon} size={17} />
                      </span>
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {badge && (
                            <span
                              className="lm-num ml-auto rounded-[var(--radius-pill)] px-[7px] py-px text-[10.5px] font-bold"
                              style={{
                                background: `rgb(${badgeRgb} / 14%)`,
                                color: badgeFg,
                              }}
                            >
                              {badge}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={toggleNav}
            className={cn(
              'lm-hov flex h-[46px] items-center gap-[10px] border-0 border-t text-[12.5px] font-semibold',
              collapsed ? 'justify-center px-0' : 'justify-start px-[17px]',
            )}
            style={{ borderColor: 'var(--border)', color: 'var(--muted-fg)' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'ic-chevright' : 'ic-chevleft'} size={17} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </aside>
        )}

        {/* -------------------------------------------------------- view */}
        {/*
          * TRANSPARENT, so `body`'s fixed gradient shows through.
          *
          * It painted `--bg` flat, which put an opaque sheet over the ground and left every
          * translucent panel with nothing to be translucent against — the glass read as a
          * slightly lighter grey box. The gradient is on `body` and fixed precisely so it
          * does not move while this scrolls.
          */}
        {/*
          * THE CONTENT COLUMN. `<main>` and the dock beneath it, stacked.
          *
          * The wrapper exists so the dock can sit below the scrolling pane without being
          * positioned: a `fixed` dock would have to be inset by the left rail's width AND
          * the quick-links rail's, both of which change when either collapses, which is
          * three numbers that must agree at every breakpoint. As a flex child it is none.
          *
          * THE INVARIANTS THIS ROW DOCUMENTS STILL HOLD, and they are why the classes are
          * split the way they are. `min-w-0` moves to the wrapper, which is now the flex
          * child of the row; `min-h-0` is what lets `<main>` scroll rather than stretching
          * the column; and `<main>` keeps `overflow-y-auto` and nothing else gains it, so it
          * remains the one scroll container in the shell. Its background stays transparent
          * so the body wash shows through — painting it would flatten every glass surface.
          */}
        {/* `relative`: the ARKAI dock floats over the bottom of <main> (Home), as the design has it. */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <main
            id="lm-main"
            /* -1 so the skip link can move focus HERE. Without it the browser scrolls to the
               element but leaves focus on the link, so the next Tab returns to the rail —
               the jump appears to work and does nothing for the keyboard. */
            tabIndex={-1}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto"
            style={{ background: 'transparent' }}
          >
            {children}
          </main>
          {/* ARKAI's dock belongs to the Home screen only (owner, 2026-10-16), as in the design;
              every other screen gets its full height back. It floats over <main> with a fade,
              and Home leaves room under its last row for it (`home.css`). */}
          {PRODUCT.aiDock && tab === 'home' && (
            <Suspense fallback={null}>
              <AiDock />
            </Suspense>
          )}
        </div>

        {/*
          * The quick-links rail, mirroring the left one rather than floating over the page.
          *
          * It ships collapsed to a 44px strip — see the component's own header for the
          * arithmetic, which is the whole reason it is not open by default: this console runs
          * a left rail the source design does not have, and two 300-400px rails leave a
          * fleet table 626px on a laptop.
          *
          * Behind the same `hiddenItems` the left rail filters with, so the two can never
          * disagree about what this reader may open.
          */}
        {PRODUCT.quickLinks && (
          <Suspense fallback={null}>
            <QuickLinks tab={tab} onTab={onTab} hiddenItems={hiddenItems} />
          </Suspense>
        )}
      </div>
    </div>
  );
}

/**
 * One KPI in the page header strip.
 *
 * `hint` is the second line — a unit, a qualifier, the thing that stops a bare number being
 * ambiguous. `tone` paints the VALUE only, never the label: a red label reads as "this
 * measurement is broken" where a red value reads as "this measurement is bad".
 */
export interface PageStat {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'ok' | 'warn' | 'danger';
  /** Hover text. Say what the number counts, not what it is called again. */
  title?: string;
}

/**
 * Page container.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HEADER IS ONE ROW, AND IT USED TO BE THREE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It was a 19px title, a 13.5px description on its own line beneath it, and `actions`
 * floated right — about 76px of chrome before any content, of which the right two-thirds
 * was empty on every page that passed no actions. Measured on the Overview screenshot: the
 * title row ended at x=420 on a 1512px viewport.
 *
 * Now: title, description INLINE after it, and a `stats` strip that fills the space to the
 * right. That reclaims roughly 30px of vertical on every screen in the product and puts
 * the numbers an operator came for on the first line rather than in a card below the fold.
 *
 * The description stays rather than becoming a tooltip. These pages answer questions an
 * operator does not necessarily know they are asking — "reconstructed from alarm intervals,
 * 30-minute buckets" is the difference between reading a chart correctly and misreading it
 * — and a caption nobody hovers is a caption nobody reads. It wraps below the title on a
 * narrow viewport, where the inline form would squeeze the title to two words.
 */
export function Page({
  title,
  desc,
  actions,
  stats,
  fill,
  stickyHeader,
  children,
}: {
  title: string;
  /**
   * The caption beside the title. A plain string on almost every screen.
   *
   * WIDENED FROM `string` so a page can put FIGURES in its own caption rather than in a band
   * beneath the header. The site hub is the case that needed it: its capacity, load and
   * commissioning year used to render in a bordered strip under the sub-nav, which cost a
   * whole horizontal band to say what the header was already the place for — and read as a
   * fourth row of chrome above content that starts below the fold.
   *
   * INLINE ELEMENTS ONLY. This renders inside a `<p>`, so a `<div>` here is invalid HTML and
   * the browser will close the paragraph early, leaving the rest of the caption outside the
   * element it is styled by. Spans, and spans of spans.
   */
  desc?: ReactNode;
  actions?: ReactNode;
  /**
   * The numbers this page exists to show, in the header rather than in a card.
   *
   * Optional, and most pages pass none — the row then collapses to title plus actions,
   * which is still one line shorter than it was.
   */
  stats?: readonly PageStat[];
  /**
   * ONE VIEWPORT, WITH THE TABLE AS THE SCROLLER.
   *
   * The default page grows to whatever its content needs and lets the browser scroll the
   * body. That is right for a report and wrong for a QUEUE: the parameter catalogue is 633
   * rows worked top to bottom with a modal per row, so the filter bar and the result count
   * scroll away exactly when they are needed most, and every classification costs a scroll
   * back up to re-filter.
   *
   * `fill` inverts it. The page becomes exactly the viewport, the chrome is pinned, and the
   * ONE thing that scrolls is the region the caller marks — which then owns both axes, so a
   * sticky `<thead>` and a sticky first column can finally both hold at once.
   *
   * WHY THIS IS A VARIANT AND NOT A SECOND COMPONENT: five screens want it — this one, the
   * audit trail (which asks the API for up to 500 rows), alarms, findings and telemetry —
   * and `UsersView` already demonstrated what happens when a screen hand-rolls its own page
   * shell instead: different padding from the rest of the console, a title two pixels off
   * every other title, and no access to the stats row.
   *
   * The caller is responsible for one thing, and it is the one everybody gets wrong: the
   * scrolling child needs `min-height: 0`. A flex child refuses to shrink below its content
   * height by default, so without it the child grows, the `overflow: hidden` here clips it,
   * and the rows below the fold become simply unreachable rather than scrollable.
   */
  fill?: boolean;
  /**
   * Pins the header row (title, caption, stats, actions) to the top of `main`'s scroll area
   * instead of letting it scroll away with the page.
   *
   * Opt-in and narrow on purpose — most pages here are short enough that a sticky header
   * would just be a second thing painted over content for no reason, and the header's own
   * docblock above is proud of having gotten this down to one row; a sticky variant is not
   * licence to grow it back into a fourth one. It exists for the site hub, whose Analytics
   * tab can run several screens tall and whose header carries the one control — the time
   * range — every card on that tab reads from. Solid `--card`, not `lm-glass`: the blur is
   * reserved for the app's own header and rail (see `Shell`'s docblock on that), and a card
   * background is what `HeaderStatButton.tsx` already treats as `lm-glass`'s resting state.
   */
  stickyHeader?: boolean;
  children: ReactNode;
}): ReactNode {
  const toneVar = (t: PageStat['tone']): string =>
    t === 'danger' ? 'var(--danger)' : t === 'warn' ? 'var(--warn)' : t === 'ok' ? 'var(--success)' : 'var(--fg)';

  /*
   * EVERY PAGE LOADS WITH THE LOGO. While anything is being read for the first time (a query
   * with no answer yet, `pending`), the product's animated mark sits beside the title. A
   * background refresh of data already on screen does not count: it has an answer, and
   * animating the logo every poll would teach the operator to ignore it. It waits briefly so a
   * fast page never blinks, then stays long enough to draw and fill, then fades (`useLoaderPhase`).
   * The cards keep their own skeletons, which say where the answer goes.
   */
  const firstLoads = useIsFetching({ predicate: (q) => q.state.status === 'pending' });
  const loader = useLoaderPhase(firstLoads > 0);

  return (
    <div
      className={`lm-slidein${fill === true ? ' flex flex-col' : ' mx-auto'}`}
      style={{
        maxWidth: 'var(--page-max)',
        /* No bottom pad in fill mode: 60px of air below a pinned footer is 60px stolen from
           the only thing on the page that can scroll. `dvh`, not `vh` — mobile browser
           chrome changes the viewport, and `vh` measures the wrong one. */
        padding: fill === true
          ? 'var(--page-pad-y) var(--page-pad-x) var(--page-pad-x)'
          : 'var(--page-pad-y) var(--page-pad-x) 60px',
        ...(fill === true ? { height: '100dvh', overflow: 'hidden' } : {}),
      }}
    >
      {/*
        THE HEADER READS THE TYPE SCALE, and that is what turned it into a header.
        
        It used to set `text-[17px]` for the title and `text-[17px]` for the stat numbers, so
        the name of the page and the numbers on it were THE SAME SIZE. Nothing led. A screen
        where every level is within four pixels of every other level reads as uniformly small
        no matter how carefully the levels were assigned, which is the real complaint behind
        "the font is too small" — the fix is a top end to the scale, not a bigger floor.
        
        Title at --fs-xl, numbers at --fs-display, labels at --fs-micro: a 28px figure over an
        11px tracked label is a stat tile, and a 22px title above them is the page.
      */}
      <div
        className="mb-[16px] flex flex-wrap items-center gap-x-[14px] gap-y-2"
        style={{
          ...(fill === true ? { flex: 'none' } : undefined),
          /* `main` is this row's nearest scrolling ancestor in every non-fill page (see
             `Shell.tsx`'s own `<main className="... overflow-y-auto">`), so `top: 0` pins it
             to the top of the visible scroll area, not to the document. Negative margin plus
             matching padding extends the sticky surface a few pixels past the row's own
             content so a card scrolling under it does not visibly clip against the text
             baseline — the same trick `--page-pad-x` already uses on the sides. */
          ...(stickyHeader === true
            ? {
                position: 'sticky',
                top: 0,
                zIndex: 5,
                background: 'var(--card)',
                marginInline: `calc(var(--page-pad-x) * -1)`,
                paddingInline: 'var(--page-pad-x)',
                paddingTop: 'var(--page-pad-y)',
                paddingBottom: 12,
                borderBottom: '1px solid var(--border)',
              }
            : undefined),
        }}
      >
        {/* `baseline` so the title and its caption sit on one line rather than centring
            against each other and looking misaligned. */}
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px]">
          <div className="flex min-w-0 items-center gap-[10px]">
            <h1 className="lm-t-xl min-w-0 font-bold tracking-[-0.02em]">{title}</h1>
            {/*
              THE SLOT IS ALWAYS THERE. A loader inserted beside the title only while loading would
              push the caption sideways when it arrived and back when it left, a shift on every
              page load. A 26px box reserved after the title costs a little air, and the title and
              the caption never move.
            */}
            <span className="grid size-[26px] shrink-0 place-items-center" aria-hidden={loader === 'off'}>
              {loader !== 'off' && (
                <span className={loader === 'out' ? 'lm-loader-out' : 'lm-loader-in'}>
                  <BrandLoader size={26} inline label={`Loading ${title}`} />
                </span>
              )}
            </span>
          </div>
          {desc && (
            /* `min-w-0`, defensively: a flex item's default `min-width: auto` asks the
               browser to reserve room for its widest unbreakable run before wrapping the
               rest, and the site hub's caption is the longest `desc` in the product — three
               identity fields plus up to three figures. Without this, a narrow window can
               end up reserving more width than the caption actually needs to start
               wrapping, pushing the row wider than the viewport instead of breaking sooner. */
            <p className="lm-t-sm min-w-0" style={{ color: 'var(--muted-fg)' }}>
              {desc}
            </p>
          )}
        </div>

        {/*
          * `ml-auto` on the trailing cluster rather than `justify-between` on the row: with
          * `justify-between` and no stats, a lone action button is pushed to the far edge
          * while the title sits at the far left and the 900px between them is the emptiness
          * this layout is fixing. Auto-margin keeps the cluster right-aligned and lets it
          * sit next to the title when there is little of it.
          */}
        {/*
          THE STATS AND THE ACTION ARE NOT THE SAME KIND OF THING, so they do not sit at the
          same spacing.

          At `gap-x-[10px]` a 28px stat figure landed 10px from the primary button, which read
          as a four-item group with one item styled oddly rather than as "here are the numbers,
          and here is the thing you can do". `gap-x-[22px]` between the tiles themselves and a
          hairline before the action is what separates reading from acting.
        */}
        {(stats !== undefined || actions !== undefined) && (
          <div className="ml-auto flex flex-wrap items-center gap-x-[22px] gap-y-2">
            {stats?.map((s) => (
              <div key={s.label} className="min-w-0" title={s.title ?? undefined}>
                <div
                  className="lm-t-micro font-bold uppercase tracking-[0.1em]"
                  style={{ color: 'var(--muted-fg)' }}
                >
                  {s.label}
                </div>
                <div className="flex items-baseline gap-[5px]">
                  <span
                    className="lm-num lm-t-display font-bold leading-none tracking-[-0.02em]"
                    style={{ color: toneVar(s.tone) }}
                  >
                    {s.value}
                  </span>
                  {s.hint && (
                    <span className="lm-t-sm" style={{ color: 'var(--muted-fg)' }}>
                      {s.hint}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {actions !== undefined && stats !== undefined && stats.length > 0 && (
              <span
                aria-hidden
                className="h-[26px] w-px self-center"
                style={{ background: 'var(--border)' }}
              />
            )}
            {actions}
          </div>
        )}
      </div>
      {/*
        `min-h-0` on the wrapper as well as on the caller's scroller. Both are needed: this
        one lets the wrapper shrink inside the page, and the caller's lets the scroller
        shrink inside the wrapper. Miss either and the overflow is clipped instead of
        scrolled.
      */}
      {fill === true ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
